// Periodic /health pings for the five MVP services + TestUi self. Returns a map of
// service name → "up" | "down" | "checking" so the header can render status pills.
// SERVICE_URLS is exported so the UI can render the URL in tooltips and the
// down-services banner can include actionable links.

import { useEffect, useState } from "react";
import { URLS } from "../api/client";
import { pingHealth } from "../api/health";

export type ServiceName =
  | "TestUi"
  | "LiveCandles"
  | "HistoricalCandles"
  | "HistoricalIndicators"
  | "LiveIndicators"
  | "Orchestrator";

export type HealthState = "up" | "down" | "checking";

export const SERVICE_URLS: Record<ServiceName, string> = {
  TestUi: URLS.testUi,
  LiveCandles: URLS.liveCandles,
  HistoricalCandles: URLS.historicalCandles,
  HistoricalIndicators: URLS.historicalIndicators,
  LiveIndicators: URLS.liveIndicators,
  Orchestrator: URLS.orchestrator,
};

const POLL_INTERVAL_MS = 5000;

export function useServiceHealth(): Record<ServiceName, HealthState> {
  const [state, setState] = useState<Record<ServiceName, HealthState>>(() =>
    Object.fromEntries(Object.keys(SERVICE_URLS).map((k) => [k, "checking"])) as Record<ServiceName, HealthState>,
  );

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      // Per-service AbortController so a slow-to-fail service can't time-out the
      // others (or be confused with them). Each fetch has its own 3.5s budget,
      // tick interval is 5s, so a stuck service still recovers cleanly.
      const entries = await Promise.all(
        (Object.entries(SERVICE_URLS) as [ServiceName, string][]).map(async ([name, url]) => {
          const ac = new AbortController();
          const timeoutId = window.setTimeout(() => ac.abort(), 3500);
          try {
            const ok = await pingHealth(url, ac.signal);
            return [name, ok ? "up" : "down"] as const;
          } finally {
            window.clearTimeout(timeoutId);
          }
        }),
      );
      if (!cancelled) {
        setState(Object.fromEntries(entries) as Record<ServiceName, HealthState>);
      }
    };

    tick();
    timer = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, []);

  return state;
}
